import { addScreenSizeListener, getEnvironmentState } from "./environment";
import { Vector } from "./vector";

const MAX_GRAVITY = 0.0038;

function waitNFrames(n: number, callback: () => void) {
  requestAnimationFrame(() => {
    if (n <= 1) callback();
    else waitNFrames(n - 1, callback);
  });
}

export class Gravity {
  private maxGravity = MAX_GRAVITY;
  private val = new Vector(0, MAX_GRAVITY);
  private enabled_ = false;
  private toggleListeners_: Array<() => void> = [];
  private deviceOrientationSupported_ = false;
  private orientationAngle = screen.orientation?.angle ?? 0;

  // Not guaranteed to be correct by the time it resolves, but will resolve
  // within a short amount of time of the page loading.
  readonly orientationSupportPromise: Promise<boolean>;

  constructor() {
    window.addEventListener('deviceorientation', e => {
      if (e.beta === null || e.gamma === null) return;

      const betaRad = e.beta / 180 * Math.PI;
      this.val.y = Math.sin(betaRad) * this.maxGravity;
      const gammaRad = e.gamma / 180 * Math.PI;
      this.val.x = Math.sin(gammaRad) * this.maxGravity;

      // Positive: b: -176, g: -82
      // Negative: b: 3 g: -77
      //           b: -153, g: 51
      const xIsPositive = (e.beta >= -90 && e.beta < 90 && e.gamma > 0) || (e.beta > 90  && e.gamma < 0) || (e.beta < -90 && e.gamma < 0);
      this.val.x = (xIsPositive ? 1 : -1) * Math.abs(this.val.x);
      this.val.angle += this.orientationAngle / 180 * -Math.PI;
    });
    screen.orientation.addEventListener('change', () => {
      const newAngle = screen.orientation?.angle ?? 0;
      this.val.angle += (newAngle - this.orientationAngle) / 180 * -Math.PI;
      this.orientationAngle = newAngle;
    });

    this.orientationSupportPromise = new Promise((resolve) => {
      let resolved = false;
      waitNFrames(8, () => {
        if (!resolved) {
          resolve(false);
          resolved = true;
        }
      });
      window.addEventListener('deviceorientation', e => {
        if (resolved) return;
        this.deviceOrientationSupported_ = e.beta !== null && e.gamma !== null;
        resolve(this.deviceOrientationSupported_);
        resolved = true;
      }, { once: true });
    });

    this.updateMaxGravity();
    addScreenSizeListener(() => {
      const prevMax = this.maxGravity;
      this.updateMaxGravity();
      if (prevMax !== this.maxGravity) {
        this.val.velocity *= this.maxGravity / prevMax;
      }
    });
  }

  private updateMaxGravity() {
    this.maxGravity = MAX_GRAVITY * getEnvironmentState().speedScale;
  }

  get deviceOrientationSupported(): boolean {
    return this.deviceOrientationSupported_;
  }

  get enabled(): boolean {
    return this.enabled_;
  }

  set enabled(val: boolean) {
    this.enabled_ = val;
    for (const fn of this.toggleListeners_) {
      fn();
    }
  }

  get percent(): number {
    if (!this.enabled_) return 0;
    return this.amount / this.maxGravity;
  }

  addToggleListener(fn: () => void) {
    this.toggleListeners_.push(fn);
  }

  get x(): number {
    return this.val.x;
  }

  get y(): number {
    return this.val.y;
  }

  get amount(): number {
    return this.val.velocity;
  }

  get angle(): number {
    return this.val.angle;
  }

  get isFallingDown() {
    return this.val.y > 0;
  }

  get isFallingUp() {
    return this.val.y < 0;
  }

  get isFallingLeft() {
    return this.val.x < 0;
  }

  get isFallingRight() {
    return this.val.x > 0;
  }
}

export const gravity = new Gravity();
